import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Award, 
  Users, 
  Clock, 
  Globe, 
  CheckCircle, 
  Star,
  Briefcase,
  Shield
} from 'lucide-react';

const WhyChooseSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const features = [
    {
      icon: Award,
      title: "Industry Leading",
      description: "25+ years of experience in cybersecurity, digital forensics, and intelligence operations"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Highly trained professionals with military, intelligence, and cybersecurity backgrounds"
    },
    {
      icon: Clock,
      title: "24/7 Response",
      description: "Round-the-clock emergency response for critical security incidents and investigations"
    },
    {
      icon: Globe,
      title: "Global Coverage",
      description: "Global service coverage with local presence and rapid deployment capabilities"
    },
    {
      icon: Briefcase,
      title: "Classified Clearance",
      description: "Security-cleared personnel for handling sensitive and classified government operations"
    }
  ];

  const capabilities = [
    "Advanced Persistent Threat (APT) Detection",
    "Incident Response & Digital Forensics",
    "Counter-Intelligence Operations",
    "Vulnerability Assessment & Penetration Testing",
    "Data Recovery & Analysis",
    "Surveillance & Counter-Surveillance",
    "Drone Operations & Aerial Intelligence",
    "Cybersecurity Training & Consultation"
  ];

  const partnerLogos = [
    { file: 'MaharashtraPolice.png', name: 'Maharashtra Police' },
    { file: 'IndianArmy.png', name: 'Indian Army' },
    { file: 'NCB.png', name: 'NCB' },
    { file: 'MumbaiPolice.png', name: 'Mumbai Police' },
    { file: 'ThanePolice.png', name: 'Thane Police' },
    { file: 'ThaneRuralPolice.png', name: 'Thane Rural Police' },
    { file: 'PunePolice.png', name: 'Pune Police' },
    { file: 'NagpurPolice.png', name: 'Nagpur Police' },
    { file: 'DockerFactory.png', name: 'Docker Factory' },
    { file: 'IntechOnline.png', name: 'Intech Online' },
    { file: 'Toothsi.png', name: 'Toothsi' },
    { file: 'AssociateDecor.png', name: 'Associate Decor' },
  ];

  return (
    <section className="section-padding bg-near-black relative overflow-hidden" ref={ref}>
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="heading-font text-5xl md:text-6xl font-bold mb-6">
            Why Choose <span className="text-royal-gold">CIARL</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            As a premier cybersecurity and intelligence organization, we combine cutting-edge technology 
            with unmatched expertise to protect what matters most to our nation and its institutions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="glass-morphism p-6 rounded-16 hover:border-royal-gold transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-royal-gold to-gold-light rounded-12 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-near-black" />
                  </div>
                  <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Capabilities & Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Hero Image */}
            <div className="relative rounded-24 overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/7688543/pexels-photo-7688543.jpeg"
                alt="Security Operations Center"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
            </div>

            {/* Capabilities List */}
            <div className="glass-morphism p-8 rounded-24">
              <h3 className="heading-font text-2xl font-semibold text-royal-gold mb-6">
                Core Capabilities
              </h3>
              <div className="space-y-3">
                {capabilities.map((capability, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.05 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                    <span className="text-gray-200 text-sm">{capability}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20"
        >
          <div className="glass-morphism p-8 rounded-24 text-center">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center">
                  <Star className="w-8 h-8 text-near-black" />
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-royal-gold">Trusted Partner</div>
                  <div className="text-gray-300">Government & Enterprise</div>
                </div>
              </div>
              
              <div className="h-16 w-px bg-gray-600 hidden md:block" />
              
              <div className="text-center">
                <div className="text-3xl font-bold text-off-white">25+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
              
              <div className="h-16 w-px bg-gray-600 hidden md:block" />
              
              <div className="text-center">
                <div className="text-3xl font-bold text-off-white">100+</div>
                <div className="text-gray-300">Successful Operations</div>
              </div>
              
              <div className="h-16 w-px bg-gray-600 hidden md:block" />
              
              <div className="text-center">
                <div className="text-3xl font-bold text-off-white">24/7</div>
                <div className="text-gray-300">Emergency Response</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Partner Logos Grid */}
      {/* <div className="mt-16">
        <div className="glass-morphism rounded-24 py-6 px-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 items-center justify-center">
            {partnerLogos.map((logo, idx) => (
              <div key={logo.file + idx} className="flex flex-col items-center">
                <img
                  src={require(`../assets/partners/${logo.file}`)}
                  alt={logo.name}
                  className="h-28 w-28 object-contain drop-shadow-lg bg-white/80 rounded-lg p-4 mx-auto"
                  style={{ minWidth: '80px', minHeight: '80px' }}
                />
                <span className="mt-2 text-sm text-gray-200 text-center font-medium">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div> */}
    </section>
  );
};

// Add marquee animation to global CSS (e.g., in App.css or index.css):
// .animate-partner-marquee {
//   animation: partner-marquee 30s linear infinite;
// }
// @keyframes partner-marquee {
//   0% { transform: translateX(0); }
//   100% { transform: translateX(-50%); }
// }

export default WhyChooseSection;